import { TimeEntriesBulkBars } from "@/components/time-entries/list/bulk-bars";
import { buildMainColumns, isUnassigned } from "@/components/time-entries/list/columns";
import { FilterDropdowns } from "@/components/time-entries/list/filters";
import { InboxTableRow } from "@/components/time-entries/list/rows";
import { timeEntriesSearchSchema } from "@/components/time-entries/list/types";
import type {
  InboxResponse,
  LoaderData,
  MainRow,
  SelectionRow,
  SortKey,
  TimeEntriesResponse,
  TimeEntriesSearch,
} from "@/components/time-entries/list/types";
import { useBulkActions } from "@/components/time-entries/list/use-bulk-actions";
import { useRowSelection } from "@/components/time-entries/list/use-selection";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { useUrlTableSort } from "@/components/ui/sortable-table";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { formatHours } from "@/lib/utils";
import {
  Link,
  createFileRoute,
  useLoaderData,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * `/time-entries-all` — the whole team's time, for admins and accountants.
 *
 * Clockify is where the team logs time; this page is the one list of
 * those records. Most rows are *linked* — the sync (or a manual Hub
 * entry) attached them to a project / work item, so they carry a real
 * Hub TimeEntry that mirrors to GitLab, with approval + billing state.
 * The rest are *unassigned* — Clockify entries the sync pulled but
 * couldn't tie to a work item; they are pinned to the top of the list
 * with an inline picker so they can be linked without leaving the
 * page. Once linked they become an ordinary row.
 *
 * One table, one set of filters (search / user / project / date
 * range). Linked rows paginate; the unassigned rows sit above the
 * first page because they are unfinished work that needs attention.
 *
 * Everything that makes this page powerful — bulk bars, the admin edit
 * modal, cross-user selection, the discarded-triage rows — is also what
 * makes it the wrong page for someone who just wants to see their own
 * week. That is `/time-entries`, which is deliberately a fraction of
 * this.
 *
 * Linked-row mutations hit `POST /api/time-entries/bulk` (single-row
 * actions pass a one-element `ids` array); assigning / discarding an
 * unassigned row hits `/api/clockify-inbox`.
 */

export const Route = createFileRoute("/_app/time-entries-all")({
  validateSearch: timeEntriesSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    userId: search.userId,
    projectId: search.projectId,
    workItemId: search.workItemId,
    overhead: search.overhead,
    approval: search.approval,
    billing: search.billing,
    tag: search.tag,
    from: search.from,
    to: search.to,
    sort: search.sort,
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
    showDiscarded: search.showDiscarded,
  }),
  loader: async ({ deps }): Promise<LoaderData> => {
    // Linked time entries — paginated, server-sorted.
    const teParams = new URLSearchParams();
    if (deps.q) teParams.set("q", deps.q);
    if (deps.userId) teParams.set("userId", deps.userId);
    if (deps.projectId) teParams.set("projectId", deps.projectId);
    if (deps.workItemId) teParams.set("workItemId", deps.workItemId);
    if (deps.overhead) teParams.set("overhead", deps.overhead);
    if (deps.approval) teParams.set("approval", deps.approval);
    if (deps.billing) teParams.set("billing", deps.billing);
    if (deps.tag) teParams.set("tag", deps.tag);
    if (deps.from) teParams.set("from", deps.from);
    if (deps.to) teParams.set("to", deps.to);
    if (deps.sort) teParams.set("sort", deps.sort);
    if (deps.dir) teParams.set("dir", deps.dir);
    if (deps.page) teParams.set("page", String(deps.page));
    if (deps.pageSize) teParams.set("pageSize", String(deps.pageSize));
    const teQs = teParams.toString();

    // Discarded rows are an opt-in review view — fetched separately
    // (server keeps them on `/api/clockify-inbox?discarded=true`).
    // Live `logged` rows now ride the main listing as part of
    // pagination, so the previous separate `/api/clockify-inbox`
    // fetch is gone.
    const discardedParams = new URLSearchParams();
    if (deps.q) discardedParams.set("q", deps.q);
    if (deps.userId) discardedParams.set("userId", deps.userId);
    if (deps.projectId) discardedParams.set("projectId", deps.projectId);
    if (deps.from) discardedParams.set("from", deps.from);
    if (deps.to) discardedParams.set("to", deps.to);
    discardedParams.set("pageSize", "200");
    discardedParams.set("discarded", "true");

    const [entries, discarded] = await Promise.all([
      api.get<TimeEntriesResponse>(teQs ? `/api/time-entries?${teQs}` : "/api/time-entries"),
      deps.showDiscarded
        ? api.get<InboxResponse>(`/api/clockify-inbox?${discardedParams.toString()}`)
        : Promise.resolve({ rows: [], total: 0 } satisfies InboxResponse),
    ]);
    // `inbox` stays in the loader payload for type compatibility,
    // but it's empty now — logged rows live in `entries`.
    const inbox: InboxResponse = { rows: [], total: 0, allProjects: entries.allProjects };
    return { entries, inbox, discarded };
  },
  component: TimeEntriesPage,
});

/** Same filters as the loader's `/api/time-entries` fetch, for the
 *  "Download CSV" / "Download PDF" links — the export always matches exactly what's on
 *  screen. Pagination / sort are dropped: the report always covers
 *  every matching row, not just the current page. */
function pdfQuery(search: TimeEntriesSearch): string {
  const qs = new URLSearchParams();
  if (search.q) qs.set("q", search.q);
  if (search.userId) qs.set("userId", search.userId);
  if (search.projectId) qs.set("projectId", search.projectId);
  if (search.workItemId) qs.set("workItemId", search.workItemId);
  if (search.overhead) qs.set("overhead", search.overhead);
  if (search.approval) qs.set("approval", search.approval);
  if (search.billing) qs.set("billing", search.billing);
  if (search.tag) qs.set("tag", search.tag);
  if (search.from) qs.set("from", search.from);
  if (search.to) qs.set("to", search.to);
  return qs.toString();
}

function TimeEntriesPage() {
  const { entries: entriesData, inbox, discarded } = Route.useLoaderData();
  // Admin gate for the `invoiced` bulk option (manual backfill for
  // entries invoiced before the billing automation went live —
  // accountants stop at `to_invoice`, the run promotes).
  const { user: appUser } = useLoaderData({ from: "/_app" });
  const isAdmin = appUser.globalRole === "admin";
  const {
    entries,
    total,
    totalHours,
    page,
    pageSize,
    users,
    projects,
    workItems,
    tagFacets,
    overheadFacets,
    externalUsers,
    externalProjects,
    canSeeAll,
    managedProjectIds,
    allProjects,
    workItemsByProject,
    viewerId,
  } = entriesData;
  const managedProjectSet = new Set(managedProjectIds);
  const search: TimeEntriesSearch = Route.useSearch();
  const router = useRouter();
  const navigate = useNavigate();

  // Off-page row state cache. Populated by the "Select all matching"
  // affordance from `/api/time-entries/ids`, which returns each
  // matching row's per-bar gate fields (approvalState, billingState,
  // projectId, userId, invoiceItemId). With it the bulk-bar
  // predicates run uniformly across visible + off-page selections —
  // without it, off-page rows had unknown state and bars were either
  // shown optimistically (extra noise) or hidden conservatively
  // (missing actions).
  // Selection + off-page meta live in one small hook — see
  // ../../components/time-entries/list/use-selection.ts.
  const selection = useRowSelection<SelectionRow>();
  const { selected, offPageMeta } = selection;
  // Clear the selection whenever a filter (NOT pagination / sort)
  // changes. Stale ids carried across a filter change yield the
  // "84 visible / 146 selected" mismatch where bulk actions apply to
  // rows the user can't even see — exactly what the toolbar count
  // promises *not* to do.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selection.clear is stable, deliberately excluded
  useEffect(() => {
    selection.clear();
  }, [
    search.q,
    search.userId,
    search.projectId,
    search.workItemId,
    search.overhead,
    search.approval,
    search.billing,
    search.tag,
    search.from,
    search.to,
    search.showDiscarded,
  ]);
  // Bulk mutations (apply action / spin report) + the shared busy
  // flag live in a hook — see
  // ../../components/time-entries/list/use-bulk-actions.ts.
  const { busy, runBulk, createReport } = useBulkActions({
    canSeeAll,
    onBulkSuccess: async () => {
      selection.clear();
      await router.invalidate();
    },
    onReportSuccess: (batchId) => {
      selection.clear();
      navigate({ to: "/billing/$id", params: { id: batchId } });
    },
  });

  // Every column sorts server-side via the URL so the ordering
  // applies to the whole result, not just the current page. The
  // primitive flips back to page 1 on every header click — landing
  // on page 12 of a freshly-ordered list would be disorienting.
  const sort = useUrlTableSort({
    sortKey: search.sort ?? null,
    sortDir: search.dir ?? "desc",
    onChange: (key, dir) =>
      navigate({
        to: "/time-entries-all",
        search: { ...search, sort: key as SortKey, dir, page: undefined },
      }),
  });

  // Unassigned rows are pinned above the first page only — paging
  // deeper is browsing older linked entries, where they'd just be
  // noise. A work-item or overhead filter also hides them: an
  // unassigned row is neither tied to a work item nor an overhead
  // entry, so it can't match either filter.
  // `logged` rows now live in `entries`. The TRIAGE bucket is the
  // subset still missing a project — those render the inline
  // picker. Triaged-but-undecided rows go through the regular
  // approval path (Approve / Reject buttons in the Actions column).
  const inboxRows = entries.filter((e) => e.approvalState === "worked" && e.project === null);

  // Discarded Clockify rows are an opt-in review view — pinned above
  // the live inbox, each with a Restore action.
  const showDiscarded = !!search.showDiscarded;
  const discardedRows = showDiscarded ? discarded.rows : [];

  // One tbody, two row kinds: discarded triage rows render through
  // `InboxTableRow` (DataTable's renderRow escape hatch), everything
  // else through the column defs below. Discarded rows are pinned
  // first, matching the old hand-assembled ordering.
  const mainRows: MainRow[] = [
    ...discardedRows.map((row) => ({ kind: "discarded" as const, row })),
    ...entries.map((te) => ({ kind: "entry" as const, te })),
  ];
  const mainColumns = buildMainColumns({
    isAdmin,
    canSeeAll,
    viewerId,
    users,
    projects,
    workItems,
    allProjects,
    workItemsByProject,
    managedProjectSet,
    busy,
    runBulk,
    onRowChanged: () => void router.invalidate(),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Time Entries</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Every Clockify record in one list. Rows still awaiting a work item are pinned to the top
            — pick one and they join the list as a linked entry.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <a href={`/api/time-entries/export?${pdfQuery(search)}`} download>
              Download CSV
            </a>
          </Button>
          <Button asChild variant="outline">
            <a href={`/api/time-entries/pdf?${pdfQuery(search)}`}>Download PDF</a>
          </Button>
          <Button asChild>
            <Link to="/time-entries/new" search={{}}>
              New Entry
            </Link>
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <SearchInput placeholder="Search by work item, description or user..." />
        <FilterDropdowns
          users={users}
          projects={projects}
          workItems={workItems}
          tagFacets={tagFacets}
          overheadFacets={overheadFacets}
          externalUsers={externalUsers}
          externalProjects={externalProjects}
          canSeeAll={canSeeAll}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {total} {total === 1 ? "entry" : "entries"}
          {Number(totalHours) > 0 ? ` · ${formatHours(Number(totalHours))}` : ""}
          {inboxRows.length > 0 ? ` · ${inboxRows.length} awaiting assignment on this page` : ""}
          {showDiscarded ? ` · ${discarded.total} discarded` : ""}
        </p>
        <Button
          size="sm"
          variant={showDiscarded ? "secondary" : "outline"}
          className="h-8 text-xs"
          onClick={() =>
            router.navigate({
              to: "/time-entries-all",
              search: { ...search, showDiscarded: showDiscarded ? undefined : true },
            })
          }
        >
          {showDiscarded ? "Hide discarded" : "Show discarded"}
        </Button>
      </div>

      {/* Bulk-action bars — one stacked bar per action group. */}
      <TimeEntriesBulkBars
        selected={selected}
        offPageMeta={offPageMeta}
        entries={entries}
        viewerId={viewerId}
        canSeeAll={canSeeAll}
        isAdmin={isAdmin}
        managedProjectSet={managedProjectSet}
        busy={busy}
        runBulk={runBulk}
        createReport={createReport}
        clearSubset={selection.removeSubset}
        onChanged={() => void router.invalidate()}
        users={users}
        projects={projects}
        workItems={workItems}
        allProjects={allProjects}
        workItemsByProject={workItemsByProject}
      />

      {(() => {
        // "Select all matching" affordance — shows when the page's
        // worked rows are fully ticked but the total result set spans
        // multiple pages. One click pulls every matching id from
        // `/api/time-entries/ids` (capped at 5000) into the selection
        // so the user can act across pages without hopping through
        // pagination. If the current selection already covers the
        // full result set, we offer a counterpart that drops the
        // out-of-page ids and reverts to "this page only".
        const pageWorkedIds = entries.map((te) => te.id);
        const pageSelectedCount = pageWorkedIds.filter((id) => selected.has(id)).length;
        const hasMore = total > entries.length;
        const allOnPageSelected = pageWorkedIds.length > 0 && pageSelectedCount === entries.length;
        const allMatchingSelected = selected.size >= total && hasMore;
        if (!hasMore || (!allOnPageSelected && !allMatchingSelected)) return null;
        return (
          <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm">
            {allMatchingSelected ? (
              <>
                <span>All {total} matching entries are selected.</span>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => selection.replace(pageWorkedIds)}
                >
                  Restrict to this page
                </button>
              </>
            ) : (
              <>
                <span>All {entries.length} entries on this page are selected.</span>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={async () => {
                    try {
                      // Every list-shaped filter has to ride along or
                      // `/ids` returns a broader set than the listing's
                      // count — the user clicks "Select all 84 matching"
                      // and ends up with 146 ids if approval/billing/tag
                      // are dropped here.
                      const teParams = new URLSearchParams();
                      if (search.q) teParams.set("q", search.q);
                      if (search.userId) teParams.set("userId", search.userId);
                      if (search.projectId) teParams.set("projectId", search.projectId);
                      if (search.workItemId) teParams.set("workItemId", search.workItemId);
                      if (search.overhead) teParams.set("overhead", search.overhead);
                      if (search.approval) teParams.set("approval", search.approval);
                      if (search.billing) teParams.set("billing", search.billing);
                      if (search.tag) teParams.set("tag", search.tag);
                      if (search.from) teParams.set("from", search.from);
                      if (search.to) teParams.set("to", search.to);
                      const r = await api.get<{
                        ids: string[];
                        rows: SelectionRow[];
                      }>(`/api/time-entries/ids?${teParams.toString()}`);
                      // Preserve any selected triage rows the user
                      // already had ticked; only expand the worked-row
                      // set. Stash the off-page rows' state so the
                      // bulk-bar predicates can run on them just like
                      // on visible rows.
                      selection.addMatching(r.ids, r.rows);
                    } catch {
                      toast({
                        title: "Couldn't fetch the full result",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  Select all {total} matching entries
                </button>
              </>
            )}
          </div>
        );
      })()}

      <DataTable
        columns={mainColumns}
        rows={mainRows}
        rowKey={(r) => (r.kind === "discarded" ? `discarded-${r.row.id}` : r.te.id)}
        sort={sort}
        emptyState="No time entries yet."
        selection={{
          selected,
          onToggle: selection.toggle,
          onToggleAll: () => selection.toggleAll(entries.map((e) => e.id)),
          rowLabel: (r) => (r.kind === "entry" ? `Select entry ${r.te.id}` : "Select row"),
          allLabel: "Select all entries",
          // Discarded triage rows render read-only — they never join
          // the selection pool, so they don't count towards the
          // header checkbox either.
          selectable: (r) => r.kind === "entry",
        }}
        rowClassName={(r) =>
          r.kind === "entry" && isUnassigned(r.te)
            ? "bg-amber-100/60 dark:bg-amber-500/[0.10]"
            : undefined
        }
        renderRow={(r) =>
          r.kind === "discarded" ? (
            <InboxTableRow
              row={r.row}
              allProjects={inbox.allProjects ?? []}
              selected={false}
              onToggleSelect={() => undefined}
              discarded
            />
          ) : undefined
        }
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="time-entries"
        basePath="/time-entries"
        searchParams={{
          q: search.q,
          userId: search.userId,
          projectId: search.projectId,
          approval: search.approval,
          billing: search.billing,
          from: search.from,
          to: search.to,
          sort: search.sort,
          dir: search.dir,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
